import { EncryptionKeys } from "controllers/EncryptionController"
import { useBoolean, useNumber } from "helpers/hooks"
import { useEffect, useState } from "react"
import { configureStore } from "stores/configureStore"
import { startLoading } from "reducers/loading"

import {
  getFileContent,
  getProjectBackupAssetURI,
  getTokenInLocalStorage,
} from "helpers/utils"
import ProgressBar from "components/ProgressBar/ProgressBar"
import {
  getUserNoteFilesMiddleware,
  postSyncNoteFileMiddleware,
  updateNotesMiddleware,
} from "pages/conversations/conversations.api"
import ConversationSyncTable from "./ConversationSyncTable"
import moment from "moment"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { postFileHashKeyMiddleware } from "pages/projects/services/api"
import { cloneDeep } from "lodash"
import { CHUNK_SIZE_ENCRYPT, encryptionGCMHelper } from "helpers/encryption-gcm"

const ConversationSync = (props: {
  onchangeDoneSyncConversationNote: (isDone: boolean) => void
  isStartSync: boolean
}) => {
  const isLoading = useBoolean()
  const isShowProgressBar = useBoolean(false)
  const totalNumber = useNumber(0)
  const doneNumber = useNumber(0)
  let log = "File path, Hash of raw, Hash after decrypting, Status, Time"
  const [data, setData] = useState<any>([])
  const [notes, setNotes] = useState<
    {
      note: string
      conversation_id: string
      id: string
    }[]
  >([])
  useEffect(() => {
    // console.log(props.isStartSync)
    if (!props.isStartSync) return
    const pendingNumber = data.filter((item) => !item.is_synced).length
    if (pendingNumber === 0) {
      props.onchangeDoneSyncConversationNote(true)
      localStorage.setItem("isDoneSyncConversationNote", "1")
    } else {
      props.onchangeDoneSyncConversationNote(false)
      localStorage.setItem("isDoneSyncConversationNote", "0")
      {
        handleOnclickSyncFile()
      }
    }
  }, [props.isStartSync])

  const getConversationNoteFiles = async () => {
    const userNoteFiles = await getUserNoteFilesMiddleware()
    const { files, notes } = userNoteFiles
    if (
      !files.filter((item) => !item.is_synced).length &&
      !notes.filter((item) => !item.is_synced).length
    ) {
      props.onchangeDoneSyncConversationNote(true)
      localStorage.setItem("isDoneSyncConversationNote", "1")
    }

    totalNumber.setValue(files.length)
    doneNumber.setValue(files.filter((item) => item.is_synced).length)
    let newFiles: {
      file_key: string
      is_synced: boolean
    }[] = []
    if (notes.length) {
      newFiles.push({
        file_key: "Note",
        is_synced:
          notes.filter((note) => note.is_synced).length === notes.length
            ? true
            : false,
      })
      setNotes(notes.filter((note) => !note.is_synced))
    }
    newFiles = newFiles.concat(files)
    setData(newFiles)
    isShowProgressBar.setValue(false)
  }

  useEffect(() => {
    getConversationNoteFiles()
  }, [])

  const syncConversationNoteFiles = async (
    files: { file_key: string; is_synced: boolean }[]
  ) => {
    const userVaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""
    const userId = localStorage.getItem("user_id") || ""
    const privateKey = await encryptionGCMHelper.getUserPrivateKey(userId)
    if (!privateKey) {
      toast(
        <LabelNotificationPage
          messenger="Private key not found!"
          type="error"
        />
      )
      return
    }
    const tokenLocal = getTokenInLocalStorage()
    console.log("-------START SYNC CONVERSATION NOTE FILE-----------")
    for (let j = 0; j < files.length; j++) {
      const file = files[j]
      if (file.is_synced) {
        continue
      }

      const fileUrl = getProjectBackupAssetURI(file.file_key)
      const fileContentResponse = await getFileContent(fileUrl, true)
      let fileData: any = fileContentResponse.content
      const contentType = fileContentResponse.content_type
      if (fileContentResponse.content_type === "") {
        continue
      }
      const fileKeyParts = file.file_key.split("/")
      const fileName = fileKeyParts[fileKeyParts.length - 1]
      let newFile: any = new File([fileData], fileName, {
        type: contentType,
      })
      fileData = null // Clear memory
      const hashHex = await encryptionGCMHelper.getFileHash(newFile)
      const hashBuffer = new TextEncoder().encode(hashHex)
      const signature = await encryptionGCMHelper.createSignature(
        privateKey,
        hashBuffer
      )
      const signatureBase64 = encryptionGCMHelper.bufferToBase64(signature)

      const totalChunks = Math.ceil(newFile.size / CHUNK_SIZE_ENCRYPT)
      const multipartUploadPath =
        await encryptionGCMHelper.getMultipartUploadPath(tokenLocal, {
          fileName: file.file_key,
          fileType: newFile.type,
          totalChunks,
          componentHistoryId: file.file_key,
          componentType: "share-component",
        })
      if (!multipartUploadPath?.key || !multipartUploadPath?.uploadId) {
        continue
      }

      const encryptKey = await encryptionGCMHelper.importAESKey(userVaultKey)

      const etags = await encryptionGCMHelper.createEncryptedChunksStream(
        newFile,
        encryptKey,
        CHUNK_SIZE_ENCRYPT,
        multipartUploadPath,
        totalChunks
      )
      newFile = null // Clear memory
      const mergeSuccess = await encryptionGCMHelper.mergeUploadedChunks(
        tokenLocal,
        multipartUploadPath,
        etags
      )
      if (!mergeSuccess) {
        continue
      }
      await postFileHashKeyMiddleware(file.file_key, signatureBase64)
      await postSyncNoteFileMiddleware({
        key: file.file_key,
      }).then(async () => {
        doneNumber.setValue((prev) => prev + 1)
      })
    }
    console.log("-------END SYNC CONVERSATION NOTE FILE-----------")

    getConversationNoteFiles()
    // downloadLogCsv(log)
  }
  const downloadLogCsv = (data: string) => {
    try {
      const url = window.URL.createObjectURL(
        new Blob([data], {
          type: "text/csv",
        })
      )

      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", "conversation_note_log.csv")
      document.body.appendChild(link)
      link.click()
      link?.parentNode?.removeChild(link)
    } catch (error) {
      console.log(error)
    }
  }

  const handleOnclickSyncFile = async () => {
    isLoading.setValue(true)
    let isError = false
    const newFiles = cloneDeep(data)

    if (notes.length) {
      configureStore.dispatch(startLoading())
      for (const note in notes) {
        if (!isError) {
          await updateNotesMiddleware(
            notes[note].conversation_id,
            notes[note].note
          )
            .then((_res) => {})
            .catch((error) => {
              isError = true
              toast(
                <LabelNotificationPage
                  messenger={error.response?.data?.message}
                  type="error"
                />
              )
            })
        }
      }
      if (isError) {
        return
      }
      const noteIndex = newFiles.findIndex((item) => item.file_key === "Note")
      if (noteIndex > -1) {
        newFiles[noteIndex].is_synced = true
      }
    }
    isShowProgressBar.setValue(true)
    configureStore.dispatch(startLoading())

    //Sync conversation note files here
    await syncConversationNoteFiles(newFiles)
    /* ------------------------------ */
  }
  return data.length > 0 ? (
    <div className="p-6 pt-0">
      <h2 className="text-[16px] font-semibold lh-26 text-black mb-[8px] mt-[24px]">
        Sync Conversation notes status
      </h2>
      <ProgressBar
        title={"Sync note files ..."}
        isShow={isShowProgressBar.value}
        currentValue={doneNumber.value || 0}
        totalValue={totalNumber.value || 0}
      />
      <div className="text-black bg-white border border-border-gray rounded-md overflow-auto">
        <ConversationSyncTable data={data} />
      </div>
    </div>
  ) : null
}

export default ConversationSync
